import {API_BASE_URL, AUTH_SESSION_STORAGE_KEY, AUTH_TOKEN_STORAGE_KEY} from './config';

export class ApiError extends Error {
    status: number;
    message: string;

    constructor(status: number, message: string) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.message = message;
    }
}

type ApiRequestOptions = {
    method: string;
    path: string;
    body?: unknown;
    useTurnstile?: boolean;
};

let turnstileTokenProvider: (() => Promise<string>) | null = null;
let turnstileQueue: Promise<void> = Promise.resolve();
let turnstileSessionPromise: Promise<string> | null = null;

const TURNSTILE_SESSION_TOKEN_KEY = 'turnstile_session_token';
const TURNSTILE_SESSION_EXPIRY_KEY = 'turnstile_session_expiry';
const TURNSTILE_EXPIRY_MARGIN_MS = 30_000;

export function setTurnstileTokenProvider(provider: () => Promise<string>) {
    turnstileTokenProvider = provider;
}

function requestTurnstileToken(): Promise<string> {
    const tokenRequest = turnstileQueue.then(() => {
        if (!turnstileTokenProvider) {
            throw new Error('Turnstile no esta inicializado.');
        }
        return turnstileTokenProvider();
    });

    turnstileQueue = tokenRequest.then(
        () => undefined,
        () => undefined
    );
    return tokenRequest;
}

function readTurnstileSession(): string | null {
    const token = sessionStorage.getItem(TURNSTILE_SESSION_TOKEN_KEY);
    const expiresAt = Number(sessionStorage.getItem(TURNSTILE_SESSION_EXPIRY_KEY));
    if (!token || !Number.isFinite(expiresAt) || expiresAt - TURNSTILE_EXPIRY_MARGIN_MS <= Date.now()) {
        clearTurnstileSession();
        return null;
    }
    return token;
}

function clearTurnstileSession() {
    sessionStorage.removeItem(TURNSTILE_SESSION_TOKEN_KEY);
    sessionStorage.removeItem(TURNSTILE_SESSION_EXPIRY_KEY);
}

async function requestTurnstileSession(): Promise<string> {
    const cached = readTurnstileSession();
    if (cached) return cached;
    if (turnstileSessionPromise) return turnstileSessionPromise;

    turnstileSessionPromise = (async () => {
        const cloudflareToken = await requestTurnstileToken();
        const response = await fetch(`${API_BASE_URL}/api/publicos/turnstile/verificar`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Turnstile-Token': cloudflareToken,
            },
        });

        if (!response.ok) {
            throw new ApiError(response.status, 'No se pudo validar la sesión Turnstile.');
        }

        const credential = await response.json() as {token: string; expiresInSeconds: number};
        if (!credential.token || !credential.expiresInSeconds) {
            throw new Error('El backend no ha devuelto una sesión Turnstile válida.');
        }

        sessionStorage.setItem(TURNSTILE_SESSION_TOKEN_KEY, credential.token);
        sessionStorage.setItem(
            TURNSTILE_SESSION_EXPIRY_KEY,
            String(Date.now() + credential.expiresInSeconds * 1000)
        );
        return credential.token;
    })().finally(() => {
        turnstileSessionPromise = null;
    });

    return turnstileSessionPromise;
}

export async function apiRequest<T = unknown>({method, path, body, useTurnstile = false}: ApiRequestOptions): Promise<T> {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    const isLoginRequest = path === '/api/autenticacion/iniciar_sesion';
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };

    if (token && !isLoginRequest) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    if (useTurnstile) {
        const turnstileToken = await requestTurnstileSession();
        if (turnstileToken) {
            headers['X-Turnstile-Token'] = turnstileToken;
        }
    }

    const response = await fetch(`${API_BASE_URL}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (response.status === 401) {
        if (!isLoginRequest) {
            localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
            localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
            window.dispatchEvent(new Event('auth:no-autorizado'));

            if (window.location.pathname.startsWith('/administracion')) {
                window.location.href = '/administracion/iniciar-sesion';
            }
        }

        throw new ApiError(401, 'Unauthorized');
    }

    if (!response.ok) {
        if (response.status === 403 && useTurnstile) {
            clearTurnstileSession();
        }
        let message = 'Error inesperado';
        const text = await response.text();

        if (text) {
            try {
                const data = JSON.parse(text);
                message =
                    data?.message ||
                    data?.error ||
                    JSON.stringify(data) ||
                    message;
            } catch {
                message = text;
            }
        }

        throw new ApiError(
            response.status,
            `[${response.status}] ${message}`
        );
    }

    if (response.status === 204) {
        return undefined as T;
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
        return (await response.text()) as T;
    }

    return response.json() as Promise<T>;
}

export async function apiUpload<T>(path: string, body: FormData): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${path}`, {
        method: 'POST',
        headers: authHeaders(),
        body,
    });
    await ensureSuccessful(response);
    return response.json() as Promise<T>;
}

export async function apiDownload(path: string): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}${path}`, {
        method: 'GET',
        headers: authHeaders(),
    });
    await ensureSuccessful(response);
    return response.blob();
}

function authHeaders(): Record<string, string> {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    return token ? {Authorization: `Bearer ${token}`} : {};
}

async function ensureSuccessful(response: Response): Promise<void> {
    if (response.status === 401) {
        localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
        localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
        window.dispatchEvent(new Event('auth:no-autorizado'));
        throw new ApiError(401, 'Unauthorized');
    }
    if (response.ok) return;

    const text = await response.text();
    let message = text || 'Error inesperado';
    if (text) {
        try {
            const data = JSON.parse(text);
            message = data?.message || data?.error || message;
        } catch {
            // La respuesta ya contiene un mensaje de texto útil.
        }
    }
    throw new ApiError(response.status, `[${response.status}] ${message}`);
}
